import { useCallback, useRef, useState } from 'react';

import Alert from './Alert.jsx';

/**
 * The app's bottom-right toast, as a hook.
 *
 * Every page that reports the outcome of an action was hand-rolling the same three pieces: the
 * `{ status, title, message }` state, a fresh key so a second outcome restarts the timer instead of
 * inheriting the previous one's countdown, and the `<Alert>` itself. This is that, once.
 *
 *   const { showToast, toastEl } = useToast();
 *   showToast('success', 'Published', 'Store config is live.');
 *   return <>{toastEl}...</>;
 *
 * Render `toastEl` in EVERY branch a page can return — including its first-load one — or an outcome
 * handed over in a navigation is withheld until the page has finished loading.
 */
export default function useToast({ duration = 2500, dangerDuration = 6000 } = {}) {
    const [toast, setToast] = useState(null);
    const seq = useRef(0);

    const showToast = useCallback((status, title, message) => {
        setToast({ id: (seq.current += 1), status, title, message });
    }, []);

    const dismiss = useCallback(() => setToast(null), []);

    const toastEl = toast ? (
        <Alert
            key={toast.id}
            status={toast.status}
            title={toast.title}
            position="bottom-right"
            duration={toast.status === 'danger' || toast.status === 'warning' ? dangerDuration : duration}
            dismissible
            onDismiss={dismiss}
        >
            {toast.message}
        </Alert>
    ) : null;

    return { toast, showToast, dismiss, toastEl };
}
